import React from "react";
import { PerformanceType } from "../../../types/post";
import { ResultRowState, rowIsValid } from "./performanceDraft";
import { performanceTypeLabel } from "../../../types/event";
import { formatResultVariable, formatTargetValue } from "../../../utils/performanceFormat";
import PerformanceIntentBadge from "../../shared/PerformanceIntentBadge";

interface CompleteStepProps {
  performanceType: PerformanceType;
  eventName: string | null;
  trainingTypeName: string | null;
  durationMinutes: number;
  rpe: number;
  load: number;
  rows: ResultRowState[];
  notes: string;
  hasNiggle: boolean;
  editMode: boolean;
}

/** Step 5 — summary before the write. */
const CompleteStep: React.FC<CompleteStepProps> = ({
  performanceType,
  eventName,
  trainingTypeName,
  durationMinutes,
  rpe,
  load,
  rows,
  notes,
  hasNiggle,
  editMode,
}) => {
  const validRows = rows.filter(rowIsValid);
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-bold font-grotesk text-base-content">
          {editMode ? "Review changes" : "Ready to save"}
        </h3>
        <p className="text-sm text-base-content/60 font-grotesk">
          Check the summary, then tap the tick to save.
        </p>
      </div>

      <div className="rounded-md border border-base-300 bg-base-200/30 p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <PerformanceIntentBadge type={performanceType} />
            <p className="mt-2 text-lg font-bold font-grotesk text-base-content truncate">
              {eventName || performanceTypeLabel(performanceType)}
            </p>
            {trainingTypeName && (
              <p className="text-xs text-base-content/60 font-grotesk">
                {trainingTypeName}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Duration", value: `${durationMinutes} min` },
            { label: "RPE", value: String(rpe) },
            { label: "Load", value: String(load) },
          ].map((m) => (
            <div
              key={m.label}
              className="rounded-md bg-base-100 border border-base-300 px-3 py-2"
            >
              <p className="text-[11px] uppercase tracking-wider text-base-content/50">
                {m.label}
              </p>
              <p className="text-sm font-geist tabular-nums font-medium mt-0.5">
                {m.value}
              </p>
            </div>
          ))}
        </div>

        {validRows.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-base-content/50 font-grotesk">
              Results
            </p>
            {validRows.map((row) => {
              const measured = row.variables
                .filter(
                  (v, i) => row.selectedIndices.has(i) && v.measurement !== null,
                )
                .map(formatResultVariable)
                .join(" · ");
              const targets = Object.entries(row.targets ?? {})
                .map(([name, t]) => `${name} ${formatTargetValue({ name, ...t })}`)
                .join(", ");
              return (
                <div
                  key={row.key}
                  className="flex items-center justify-between gap-2 rounded-md bg-base-100 border border-base-300 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {row.activity?.name}
                      {(row.isPB || row.isSB) && (
                        <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-base-200 text-base-content/60 font-geist">
                          {[row.isPB && "PB", row.isSB && "SB"]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-base-content/60 font-geist tabular-nums truncate">
                      {measured}
                      {targets && ` · target ${targets}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {notes.trim() && (
          <p className="text-sm text-base-content/80 whitespace-pre-wrap">
            {notes.trim()}
          </p>
        )}
        {hasNiggle && (
          <span className="inline-flex items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-warning/20 text-warning font-geist">
            Niggle
          </span>
        )}
      </div>

    </div>
  );
};

export default CompleteStep;
